import { FlexContainer } from '@/ui/styled-ui';
import { CustomizableIcon, PencilStrokeIcon } from '@/utils/helpers';
import { Dialog } from 'primereact/dialog';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledDialog = styled(Dialog)`
  &.p-dialog {
    width: 550px;
    max-width: 95vw;
    border-radius: var(--medium-border-radius);
    overflow: hidden;

    .p-dialog-header {
      display: none;
    }

    .p-dialog-content {
      padding: var(--semi-big-padding);
    }
  }
`;

const StyledOptionCard = styled.button<{ $disabled?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--small-padding);
  align-items: flex-start;
  padding: var(--default-padding);
  background: var(--beige-main);
  border: 1px solid var(--gray-dark);
  border-radius: var(--box-border-radius);
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};
  text-align: left;
  transition: border-color 0.2s;

  &:hover {
    border-color: var(--gray-darker);
  }
`;

const StyledIconBadge = styled.div`
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--red-dark);
  border-radius: var(--box-border-radius);
  color: var(--white-main);
  margin-bottom: var(--xsmall-padding);

  svg {
    width: 20px;
    height: 20px;
  }

  svg path {
    stroke: var(--white-main);
  }
`;

const StyledOptionTitle = styled.h3`
  margin-top: 0;
  margin-bottom: var(--xxsmall-padding);
  font-size: var(--small-font-size);
`;

const StyledOptionDescription = styled.p`
  font-size: var(--xsmall-font-size);
  line-height: var(--xsmall-line-height);
  margin: 0;
`;

type CustomizeChoiceDialogProps = {
  visible: boolean;
  onHide: () => void;
  onAiCustomize: () => void;
  onManualCustomize: () => void;
  canAiCustomize: boolean;
  canClone: boolean;
};

export const CustomizeChoiceDialog: React.FC<CustomizeChoiceDialogProps> = ({
  visible,
  onHide,
  onAiCustomize,
  onManualCustomize,
  canAiCustomize,
  canClone,
}) => {
  const { t } = useTranslation();

  return (
    <StyledDialog
      visible={visible}
      onHide={onHide}
      blockScroll
      draggable={false}
      modal
      closable={false}
      dismissableMask
      pt={{ root: { dir: document.documentElement.dir || 'ltr' } }}
    >
      <h2>{t('content.preview.customize.title')}</h2>

      <FlexContainer gap="var(--small-padding)" align="stretch">
        <StyledOptionCard
          $disabled={!canAiCustomize}
          onClick={canAiCustomize ? onAiCustomize : undefined}
        >
          <StyledIconBadge>
            <CustomizableIcon />
          </StyledIconBadge>
          <div>
            <StyledOptionTitle>
              {t('content.preview.customize.ai.title')}
            </StyledOptionTitle>
            <StyledOptionDescription>
              {t('content.preview.customize.ai.description')}
            </StyledOptionDescription>
          </div>
        </StyledOptionCard>

        <StyledOptionCard
          $disabled={!canClone}
          onClick={canClone ? onManualCustomize : undefined}
        >
          <StyledIconBadge>
            <PencilStrokeIcon />
          </StyledIconBadge>
          <div>
            <StyledOptionTitle>
              {t('content.preview.customize.manual.title')}
            </StyledOptionTitle>
            <StyledOptionDescription>
              {t('content.preview.customize.manual.description')}
            </StyledOptionDescription>
          </div>
        </StyledOptionCard>
      </FlexContainer>
    </StyledDialog>
  );
};
